import { useState, useEffect, useCallback } from 'react';
import { fetchROIData } from '@/services/api';

function appendCommonFilters(params, filters, { includePocName = true } = {}) {
    if (filters.region) params.append('region', filters.region);
    if (filters.brand) params.append('brand', filters.brand);

    if (filters.campaign && Array.isArray(filters.campaign) && filters.campaign.length > 0) {
        filters.campaign.forEach((name) => params.append('campaign_name', name));
    } else if (filters.campaign && typeof filters.campaign === 'string') {
        params.append('campaign_name', filters.campaign);
    }

    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    if (filters.poc_id && filters.poc_id.length > 0) {
        filters.poc_id.forEach((id) => params.append('poc_id', id));
    }

    if (includePocName) {
        if (filters.poc_name && Array.isArray(filters.poc_name) && filters.poc_name.length > 0) {
            filters.poc_name.forEach((name) => params.append('poc_name', name));
        } else if (filters.poc_name && typeof filters.poc_name === 'string') {
            params.append('poc_name', filters.poc_name);
        }
    }

    if (filters.key_account) params.append('key_account', filters.key_account);
}

export function useROIData(filters = {}) {
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [upliftByRegion, setUpliftByRegion] = useState([]);
    const [upliftByBrand, setUpliftByBrand] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);

    const fetchData = useCallback(async (customPage = page, customPageSize = pageSize, isPagination = false) => {
        if (isPagination) {
            setIsTableLoading(true);
        } else {
            setIsLoading(true);
        }
        setError(null);
        try {
            const params = new URLSearchParams();
            appendCommonFilters(params, filters, { includePocName: true });
            params.append('page', customPage);
            params.append('page_size', customPageSize);
            const response = await fetchROIData(params);
            setData(response.data || []);
            setSummary(response.summary || null);
            setUpliftByRegion(response.uplift_by_region || []);
            setUpliftByBrand(response.uplift_by_brand || []);
            setTotalPages(response.total_pages || 1);
            setTotalRows(response.total_rows || 0);
        } catch (err) {
            setError(err);
        } finally {
            if (isPagination) {
                setIsTableLoading(false);
            } else {
                setIsLoading(false);
            }
        }
    }, [filters, page, pageSize]);

    // Initial load and filter changes
    useEffect(() => {
        setPage(1);
        fetchData(1, pageSize, false);
    }, [JSON.stringify(filters)]);

    // Pagination only
    useEffect(() => {
        fetchData(page, pageSize, true);
    }, [page, pageSize, fetchData]);

    // Function to fetch all ROI table data for export
    const getAllROIData = async () => {
        const params = new URLSearchParams();
        appendCommonFilters(params, filters, { includePocName: false });
        params.append('page', 1);
        params.append('page_size', 100000);
        const response = await fetchROIData(params);
        return response;
    };

    return { data, summary, upliftByRegion, upliftByBrand, isLoading, isTableLoading, error, page, setPage, pageSize, setPageSize, totalPages, totalRows, getAllROIData };
} 